import React, { useState, useEffect } from "react";
import {
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Grid,
  Box,
  Link,
  TablePagination
} from "@material-ui/core";
import MainContainer from "../../components/MainContainer";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import moment from "moment";
import { Link as RouterLink } from "react-router-dom";
import ModernDatePicker from "../../components/ModernDatePicker";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    padding: theme.spacing(3),
    borderRadius: "20px",
    boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
  },
  headerContainer: {
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  tableHead: {
    backgroundColor: theme.palette.background.default,
  },
  totalBox: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: "white",
    borderRadius: "10px",
    textAlign: "right",
  }
}));

const RevenueReport = () => {
  const classes = useStyles();
  const [revenues, setRevenues] = useState([]);
  const [dateStart, setDateStart] = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [dateEnd, setDateEnd] = useState(moment().endOf("month").format("YYYY-MM-DD"));
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [count, setCount] = useState(0);

  useEffect(() => {
    fetchRevenues();
  }, [dateStart, dateEnd, pageNumber]);

  const fetchRevenues = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ticket-revenue", {
        params: { dateStart, dateEnd, pageNumber }
      });
      setRevenues(data.revenues);
      setCount(data.count);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const calculateTotal = () => {
    return revenues.reduce((acc, rev) => acc + parseFloat(rev.value), 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  };

  return (
    <MainContainer>
      <Box className={classes.headerContainer}>
        <Typography variant="h5" className={classes.title}>Relatório de Receitas</Typography>
        <Typography variant="subtitle2" color="textSecondary">Visualize as receitas registradas por encerramento de tickets</Typography>
      </Box>

      <Paper className={classes.mainPaper}>
        <Grid container spacing={3} style={{ marginBottom: 20 }}>
          <Grid item xs={12} sm={6} md={3}>
            <ModernDatePicker
              label="Data Início"
              value={dateStart ? moment(dateStart).toDate() : null}
              onChange={(newValue) => setDateStart(moment(newValue).format("YYYY-MM-DD"))}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ModernDatePicker
              label="Data Fim"
              value={dateEnd ? moment(dateEnd).toDate() : null}
              onChange={(newValue) => setDateEnd(moment(newValue).format("YYYY-MM-DD"))}
            />
          </Grid>
        </Grid>

        <Table size="small">
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Ticket</TableCell>
              <TableCell>Atendente</TableCell>
              <TableCell>Contato</TableCell>
              <TableCell>Número</TableCell>
              <TableCell>Periodicidade</TableCell>
              <TableCell align="right">Valor</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {revenues?.map((rev) => (
              <TableRow key={rev.id}>
                <TableCell>{moment(rev.createdAt).format("DD/MM/YYYY HH:mm")}</TableCell>
                <TableCell>
                  <Link component={RouterLink} to={`/tickets/${rev.ticket.uuid}`}>
                    #{rev.ticketId}
                  </Link>
                </TableCell>
                <TableCell>{rev.user?.name || rev.ticket?.user?.name || "N/A"}</TableCell>
                <TableCell>{rev.ticket?.contact?.name}</TableCell>
                <TableCell>{rev.ticket?.contact?.number}</TableCell>
                <TableCell>{rev.periodicity || "Nenhuma"}</TableCell>
                <TableCell align="right">
                  {parseFloat(rev.value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                </TableCell>
              </TableRow>
            ))}
            {revenues.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">Nenhuma receita encontrada para o período selecionado</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[20]}
          component="div"
          count={count}
          rowsPerPage={20}
          page={pageNumber - 1}
          onPageChange={(e, p) => setPageNumber(p + 1)}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
          }
        />

        {revenues.length > 0 && (
          <Box className={classes.totalBox}>
            <Typography variant="h6">Total: {calculateTotal()}</Typography>
          </Box>
        )}
      </Paper>
    </MainContainer>
  );
};

export default RevenueReport;
